/**
 * Bridge between the new `@cortex/domain` `AuthMeResponse` shape (the
 * RBAC+ABAC+ReBAC permission model from PR #4b) and the legacy
 * `UserRole` enum that the React tree still consumes (Sidebar, profile
 * menu, the engine-injected `meta.userRole` field).
 *
 * Why an adapter rather than a full refactor:
 *
 *   - The new model is dynamic: a user can hold multiple role
 *     assignments (e.g. SeniorUnderwriter + ComplianceOfficer) and
 *     their effective permissions are the union of all assigned
 *     permission keys plus user-level overrides.
 *
 *   - The legacy UI treats `userRole` as a single enum value used for
 *     coarse layout decisions (which sidebar items to show, which
 *     avatar initial to render). Rewriting every consumer to read
 *     `permissions.keys` directly is out of scope for PR #4d (and many
 *     of those consumers will be torn out entirely in the v2 Admin
 *     Module work).
 *
 * The adapter therefore picks the **most authoritative** role slug
 * from the assigned list and maps it onto the closest legacy enum
 * value. Authority order (highest first):
 *
 *   admin-super-user           → UserRole.Superuser
 *   senior-underwriter         → UserRole.Senior
 *   junior-underwriter         → UserRole.Junior
 *   broker-admin               → UserRole.Broker
 *   senior-broker              → UserRole.Broker
 *   junior-broker              → UserRole.Broker
 *
 * Any role slug we don't recognise falls through to UserRole.Junior
 * (the most-restrictive internal role). That keeps the SPA functional
 * if Lombard introduces a new role server-side before the SPA is
 * updated, without accidentally widening privileges.
 *
 * **Permission gating** must NEVER go through this adapter. Use the
 * `permissions.keys` array on the auth context for that. The adapter
 * is presentation-only.
 */

import type { AuthMeResponse } from '@cortex/domain';
import { UserRole } from '../types';

const ROLE_PRIORITY: Readonly<Record<string, { rank: number; legacy: UserRole }>> = {
  'admin-super-user': { rank: 100, legacy: UserRole.Superuser },
  'senior-underwriter': { rank: 80, legacy: UserRole.Senior },
  'class-underwriter': { rank: 75, legacy: UserRole.Senior },
  'junior-underwriter': { rank: 60, legacy: UserRole.Junior },
  'compliance-officer': { rank: 70, legacy: UserRole.Senior },
  'broker-admin': { rank: 50, legacy: UserRole.Broker },
  'senior-broker': { rank: 40, legacy: UserRole.Broker },
  'junior-broker': { rank: 30, legacy: UserRole.Broker },
};

/**
 * Derive a single legacy `UserRole` enum value from a principal's
 * assigned roles. Returns the highest-rank match; falls back to
 * `UserRole.Junior` for unknown slugs.
 */
export const deriveLegacyRole = (roles: AuthMeResponse['permissions']['roles']): UserRole => {
  let best: { rank: number; legacy: UserRole } | null = null;
  for (const role of roles) {
    const mapped = ROLE_PRIORITY[role.slug];
    if (!mapped) continue;
    if (best === null || mapped.rank > best.rank) {
      best = mapped;
    }
  }
  return best?.legacy ?? UserRole.Junior;
};

/**
 * True when the principal's entity type is internal (Lombard /
 * Insurer). Used by the Sidebar to hide / show internal-only modules.
 */
export const isInternalEntity = (typeSlug: AuthMeResponse['user']['entity']['typeSlug']): boolean =>
  typeSlug === 'insurer';
